import React, { useEffect } from 'react';
import styles from './index.module.scss';
const AgTable = React.lazy(
  () => import(/* webpackChunkName: ['table'] */ '@navi/web-ui/lib/components/AgTable/AgTable')
);
import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { getEscalations } from '../../actions/interactionsActions';
import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { NO_ESCALATION_RECORD_COPY } from '../../constants/Overview.constant';
import { isObjectEmpty } from 'src/utils/commonUtils';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import { QueryParams } from 'src/components/interfaces';
import { ESCALATIONS_TABLE } from '../../constants/Interactions.constant';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import TableLoader from 'src/components/TableLoader/TableLoader';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import DateTime from '../DateTime';
import { IEscalationPayload } from '../../interfaces/Escalations.types';
import { ColDefsType } from '@components/AgTable/types';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import LinkIcon from '@cp/src/assets/icons/LinkIcon';

const INTERACTION_TAB = 'interaction';

const FRESH_WORKSPACE_URL = 'https://navi.freshdesk.com/a/tickets/';

const handleRedirection = (ticketId: string) => {
  window.open(`${FRESH_WORKSPACE_URL}${ticketId}`);
};

const escalationsColDefs = [
  {
    field: 'createdAt',
    headerName: 'Date & Time',
    cellRenderer: (params: ICellRendererParams) => {
      const istDate = new Date(params?.value);
      return <DateTime istDate={istDate} />;
    },
    width: '140px'
  },
  {
    field: 'title',
    headerName: 'Title of the ticket',
    width: '250px',
    wrapText: true,
    cellStyle: { wordBreak: 'break-word', padding: '8', cursor: 'auto' },
    autoHeight: true
  },
  {
    field: 'voc',
    headerName: 'Voice of Customer',
    autoHeight: true,
    wrapText: true,
    width: '220px',
    cellStyle: { cursor: 'auto' }
  },
  {
    field: 'status',
    headerName: 'Status',
    cellStyle: { cursor: 'auto' },
    cellRenderer: (params: ICellRendererParams) => {
      return <Typography variant="p4">{params.value}</Typography>;
    },
    width: '110px'
  },
  {
    field: 'vendorTicketId',
    headerName: 'Details',
    cellStyle: { cursor: 'auto' },
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <LinkIcon className="cursor-pointer" onClick={() => handleRedirection(params.value)} />
      );
    },
    width: '110px'
  }
] as unknown as ColDefsType[];

const DEFAULT_PAGE_SIZE = 10;

const Escalations = () => {
  const { loanId = '', customerId = '' } = useParams();
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );
  const { data: escalationsData, loading: escalationsLoading } = pageData?.escalations || {};
  const [searchParams] = useSearchParams();
  const { [INTERACTION_TAB]: queryParams } = readQueryParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const createPayloadForEscalations = () => {
    const { [INTERACTION_TAB]: queryParams } = readQueryParams();
    return {
      page_no: queryParams?.page || 0,
      page_size: queryParams?.size || DEFAULT_PAGE_SIZE,
      customerReferenceId: customerId
    } as IEscalationPayload;
  };

  const getEscalationsData = () => {
    const payload = createPayloadForEscalations();
    dispatch(getEscalations(loanId, customerId, payload));
  };

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_EscalationPageLand);
  }, []);

  useEffect(() => {
    getEscalationsData();
    if (!pageData?.escalations) {
      getEscalationsData();
    }
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(queryParams)) {
      getEscalationsData();
    }
  }, [searchParams]);

  const updateQueryParamsAndNavigate = (params?: QueryParams, lastUsed?: string) => {
    const url = createQueryParams({
      [INTERACTION_TAB]: { ...queryParams, ...params, lastUsed }
    });
    navigate(url, {
      replace: true
    });
  };

  const getCurrentPageNumber = (): number => {
    return Number(queryParams?.page) + 1 || 1;
  };

  const getCurrentPageSize = (): number => {
    return Number(queryParams?.size) || DEFAULT_PAGE_SIZE;
  };

  const handlePageSizeChange = (size: number) =>
    updateQueryParamsAndNavigate({ size: size.toString(), page: 0 });

  const handlePageChange = (page: number) => {
    updateQueryParamsAndNavigate({ page: (page - 1).toString() });
  };

  return (
    <div className={styles.waInteractionsWrapper}>
      <div className={styles.table}>
        <SuspenseLoader loading={!!escalationsLoading} fallBack={<TableLoader numberOfLines={6} />}>
          <AgTable
            defaultColDef={{
              suppressMovable: true,
              wrapText: true
            }}
            style={{
              height: `${window?.innerHeight - 369}`
            }}
            className="teamLeadTable"
            gridOptions={{ suppressCellSelection: true }}
            rowData={escalationsData?.data}
            columnDefs={escalationsColDefs}
            sizeColumnsToFit
            theme={'alpine'}
            noRowsOverlayComponent={() => (
              <Typography variant="p4" className={styles.emptyListText}>
                {NO_ESCALATION_RECORD_COPY}
              </Typography>
            )}
            animateRows
            paginationWrapperClasses={'paginationWrapperClasses'}
            PaginationComponent={
              <Pagination
                totalCount={escalationsData?.pages?.totalElements || 0}
                onPageChange={handlePageChange}
                currentPage={getCurrentPageNumber()}
                onPageSizeChange={handlePageSizeChange}
                pageSize={getCurrentPageSize()}
                pageNumberDropDownPosition={DropDownPosition.TOP}
              />
            }
          />
        </SuspenseLoader>
      </div>
    </div>
  );
};
export default Escalations;
